(function () {
  const CONTEXT_ID = 'gtm-datalayer-context';
  const ADD_SOURCES = new Set(['product-form-component', 'plp-add-to-cart-component', 'quick-order-list']);
  const CART_CHANGE_SOURCE = 'cart-items-component';

  /** @type {Record<string, number>} */
  let lastCartSnapshot = {};
  /** @type {Record<string, any>} */
  let lastCartLineItems = {};

  let lastViewedVariant = null;
  const catalog = {};
  const seenImpressions = new Set();
  const observedCards = new WeakSet();
  let listObserver = null;
  let ga4EventsReady = false;
  const pendingGa4Events = [];

  function ga4Item(product, quantity) {
    const category = normalizeCategory(product.category);
    const item = {
      item_id: String(product.id), item_name: product.name,
      price: Number(product.ga4Price ?? product.price), quantity: Number(quantity || product.quantity || 1),
      item_brand: product.brand, item_variant: product.variant,
      item_category: category[0], item_category2: category[1],
    };
    Object.keys(item).forEach(function (key) {
      if (item[key] == null || item[key] === '') delete item[key];
    });
    return item;
  }

  function ga4Parameters(products, context) {
    const items = products.map(product => ga4Item(product));
    return {
      currency: context && context.currencyCode || 'CZK',
      value: Number(items.reduce((sum, item) => sum + item.price * item.quantity, 0).toFixed(2)),
      items: items,
    };
  }

  function pushGa4(name, parameters, context) {
    const payload = withEventMeta({
      event: 'ga4.' + name,
      visitor: context && context.visitor,
      ga4: Object.assign({ event_name: name }, parameters),
    });
    if (!ga4EventsReady) {
      pendingGa4Events.push(payload);
      return;
    }
    pushDataLayer(payload);
  }

  function flushGa4Events() {
    // The loader calls this after queuing gtm.js (the Google tag's All Pages trigger).
    // Keep context available before GTM, but process GA4 events after its configuration.
    if (ga4EventsReady) return;
    if (!(window.dataLayer || []).some(entry => entry && entry.event === 'gtm.js')) return;
    ga4EventsReady = true;
    pendingGa4Events.splice(0).forEach(pushDataLayer);
  }

  function rememberProduct(product) {
    if (!product) return;
    const entry = { category: product.category, id: product.id };
    catalog[String(product.variantId || product.variant_id || product.id)] = entry;
    catalog[String(product.id)] = entry;
  }

  function persistCatalog() {
    try {
      const consent = window.VecickyGtmConsent && window.VecickyGtmConsent.readMirrorCookie();
      if (!consent || !consent.analytics) return;
      const previous = JSON.parse(window.localStorage.getItem('vecicky_ga4_catalog') || '{}');
      const merged = Object.assign({}, previous, catalog);
      const keys = Object.keys(merged).slice(-500);
      window.localStorage.setItem('vecicky_ga4_catalog', JSON.stringify(Object.fromEntries(keys.map(key => [key, merged[key]]))));
    } catch (error) { /* Storage may be unavailable or blocked. */ }
  }

  function pushProductView(context) {
    if (!context || !context.product || context.pageType !== 'Product') return;
    const variant = String(context.product.variantId || context.product.id);
    if (variant === lastViewedVariant) return;
    lastViewedVariant = variant;
    rememberProduct(context.product);
    persistCatalog();
    pushGa4('view_item', ga4Parameters([context.product], context), context);
  }

  function listItem(card, context) {
    try {
      const product = JSON.parse(card.dataset.ga4Item);
      const container = card.closest('[data-ga4-list-id]');
      if (!container) return null;
      const listId = container.dataset.ga4ListId || context.collectionId || context.path;
      const listName = container && container.dataset.ga4ListName || context.pageCategory;
      if (!listId || !listName) return null;
      const cards = Array.from((container || document).querySelectorAll ? (container || document).querySelectorAll('product-card[data-ga4-item]') : document.querySelectorAll('product-card[data-ga4-item]'));
      const item = {
        ...product, item_list_id: String(listId), item_list_name: listName,
        index: Math.max(0, cards.indexOf(card)),
      };
      delete item.variantId;
      rememberProduct({id:item.item_id,variantId:product.variantId,category:item.item_category});
      return item;
    } catch (error) { return null; }
  }

  function scanProductCards() {
    const context = readEnrichedContext();
    if (!context) return;
    document.querySelectorAll('product-card[data-ga4-item]').forEach(function (card) {
      if (observedCards.has(card)) return;
      observedCards.add(card);
      const item = listItem(card, context);
      if (item && listObserver) listObserver.observe(card);
    });
    persistCatalog();
  }

  function setupProductLists() {
    if (typeof IntersectionObserver !== 'undefined') {
      listObserver = new IntersectionObserver(function (entries) {
        const context = readEnrichedContext();
        const groups = {};
        entries.forEach(function (entry) {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.5) return;
          const item = listItem(entry.target, context);
          if (!item) return;
          const key = item.item_list_id + ':' + item.item_id + ':' + item.index;
          if (seenImpressions.has(key)) return;
          seenImpressions.add(key);
          listObserver.unobserve(entry.target);
          const group = groups[item.item_list_id] || (groups[item.item_list_id] = []);
          group.push(item);
        });
        Object.values(groups).forEach(function (items) {
          pushGa4('view_item_list', {items:items,item_list_id:items[0].item_list_id,item_list_name:items[0].item_list_name}, context);
        });
      }, {threshold:0.5});
    }
    scanProductCards();
    if (typeof MutationObserver !== 'undefined') {
      let scanScheduled = false;
      new MutationObserver(function () {
        if (scanScheduled) return;
        scanScheduled = true;
        setTimeout(function () { scanScheduled = false; scanProductCards(); }, 100);
      }).observe(document.documentElement, {childList:true,subtree:true});
    }
    document.addEventListener('click', function (event) {
      const anchor = event.target && event.target.closest && event.target.closest('a[href]');
      if (!anchor) return;
      const card = anchor.closest('product-card[data-ga4-item]');
      if (!card || !anchor.href || !new URL(anchor.href, window.location.href).pathname.includes('/products/')) return;
      const context = readEnrichedContext();
      const item = listItem(card, context);
      if (item) pushGa4('select_item', {items:[item],item_list_id:item.item_list_id,item_list_name:item.item_list_name}, context);
    });
  }

  function readContext() {
    const element = document.getElementById(CONTEXT_ID);
    if (!element) {
      return null;
    }

    try {
      return JSON.parse(element.textContent || '');
    } catch (error) {
      return null;
    }
  }

  function readEnrichedContext() {
    const context = readContext();
    if (!context) {
      return null;
    }

    if (
      window.VecickyGtmInternalTraffic &&
      typeof window.VecickyGtmInternalTraffic.enrichVisitor === 'function'
    ) {
      context.visitor = window.VecickyGtmInternalTraffic.enrichVisitor(context.visitor, context);
    }

    return context;
  }

  function newEventId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    return String(Date.now()) + Math.random().toString(16).slice(2);
  }

  function formatPrice(priceInCents) {
    return (Number(priceInCents) / 100).toFixed(2);
  }

  function pushDataLayer(payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }

  function withEventMeta(payload) {
    const eventID = newEventId();
    return Object.assign({}, payload, {
      eventID: eventID,
      facebook: { eventID: eventID },
    });
  }

  function normalizeCategory(category) {
    if (Array.isArray(category)) {
      return category.filter(Boolean).map(String);
    }

    if (category) {
      return [String(category)];
    }

    return [];
  }

  function mapContextProduct(product) {
    if (!product) {
      return null;
    }

    return {
      id: product.id,
      name: product.name,
      price: String(product.price),
      brand: product.brand || undefined,
      category: normalizeCategory(product.category),
      variant: product.variant || undefined,
    };
  }

  function mapCartLineItem(item) {
    return {
      id: item.sku || String(item.variant_id || item.id),
      name: item.product_title || item.title || item.name,
      price: formatPrice(item.final_price ?? item.price),
      ga4Price: Number(item.ga4Price ?? formatPrice(item.discounted_price ?? item.final_price ?? item.price)),
      quantity: String(item.quantity ?? 1),
      brand: item.vendor || undefined,
      category: normalizeCategory(item.category || item.product_type || item.type || (catalog[String(item.variant_id)] || catalog[item.sku] || {}).category),
      variant: item.variant_title || item.variant || undefined,
    };
  }

  function findCartLineItem(cart, variantId) {
    if (!cart || !Array.isArray(cart.items) || variantId == null) {
      return null;
    }

    return cart.items.find(function (item) {
      return String(item.variant_id) === String(variantId);
    });
  }

  function cartLineIdentity(item) {
    const variant = item.variant_id || item.variantId || item.id;
    const properties = Object.keys(item.properties || {}).sort().map(key => [key, item.properties[key]]);
    const plan = item.selling_plan_allocation?.selling_plan?.id || item.sellingPlanId || null;
    return JSON.stringify([String(variant), properties, plan == null ? null : String(plan)]);
  }

  function snapshotCart(cart) {
    /** @type {Record<string, number>} */
    const snapshot = {};
    /** @type {Record<string, any>} */
    const lineItems = {};

    if (!cart || !Array.isArray(cart.items)) {
      lastCartLineItems = lineItems;
      return snapshot;
    }

    cart.items.forEach(function (item) {
      const key = cartLineIdentity(item);
      snapshot[key] = Number(item.quantity) || 0;
      lineItems[key] = item;
    });

    lastCartLineItems = lineItems;
    return snapshot;
  }

  function diffCartItems(previousSnapshot, cart) {
    const added = [];
    const removed = [];

    if (!cart || !Array.isArray(cart.items)) {
      return { added: added, removed: removed };
    }

    /** @type {Record<string, any>} */
    const currentItems = {};
    cart.items.forEach(function (item) {
      currentItems[cartLineIdentity(item)] = item;
    });

    Object.keys(currentItems).forEach(function (key) {
      const item = currentItems[key];
      const previousQty = previousSnapshot[key] || 0;
      const currentQty = Number(item.quantity) || 0;

      if (currentQty > previousQty) {
        added.push({
          item: item,
          quantity: currentQty - previousQty,
        });
      } else if (currentQty < previousQty) {
        removed.push({
          item: item,
          quantity: previousQty - currentQty,
        });
      }
    });

    Object.keys(previousSnapshot).forEach(function (key) {
      if (currentItems[key]) {
        return;
      }

      removed.push({
        item: lastCartLineItems[key] || { key: key, quantity: previousSnapshot[key] },
        quantity: previousSnapshot[key],
      });
    });

    return { added: added, removed: removed };
  }

  function buildPagePayload(context) {
    const payload = {
      page: {
        type: context.pageType,
        path: context.path,
      },
      visitor: context.visitor,
      ecommerce: {
        currencyCode: context.currencyCode || 'CZK',
      },
    };

    if (context.pageCategory) {
      payload.page.category = context.pageCategory;
    }

    if (context.product) {
      payload.ga4 = ga4Parameters([context.product], context);
      payload.ecommerce.detail = {
        products: [mapContextProduct(context.product)],
      };
    }

    if (Array.isArray(context.cartItems) && context.cartItems.length > 0) {
      payload.ecommerce.checkout = {
        actionField: { step: 1 },
        products: context.cartItems.map(mapContextProduct),
      };
    }

    return payload;
  }

  function pushPageContext(context) {
    pushDataLayer(withEventMeta(buildPagePayload(context)));
  }

  function mapEcomailBasketProduct(item) {
    const product = mapCartLineItem(item);
    const mapped = {
      productId: product.id,
      name: product.name,
      price: parseFloat(product.price) || 0,
    };

    if (item.url || item.product_url) {
      mapped.url = item.url || item.product_url;
    }
    if (item.image || item.featured_image?.url) {
      mapped.img_url = item.image || item.featured_image.url;
    }
    if (product.category && product.category.length) {
      mapped.description = product.category.join(' / ');
    }

    return mapped;
  }

  function mapCartToBasketProducts(cart) {
    if (!cart || !Array.isArray(cart.items)) {
      return [];
    }

    return cart.items.map(mapEcomailBasketProduct);
  }

  function pushAddToCartFromLine(item, quantity, context, cart) {
    const product = mapCartLineItem(item);

    pushDataLayer(
      withEventMeta({
        event: 'ec.addToCart',
        ga4: ga4Parameters([Object.assign({}, product, {quantity:quantity || 1})], context),
        page: { type: context?.pageType || 'Other', path: context?.path },
        visitor: context?.visitor,
        ecommerce: {
          currencyCode: context?.currencyCode || 'CZK',
          add: {
            products: [
              Object.assign({}, product, {
                quantity: String(quantity || product.quantity || 1),
              }),
            ],
          },
          basket: {
            products: mapCartToBasketProducts(cart),
          },
        },
      })
    );
  }

  function pushRemoveFromCartFromLine(item, quantity, context, cart) {
    const product = mapCartLineItem(item);

    pushDataLayer(
      withEventMeta({
        event: 'ec.removeFromCart',
        ga4: ga4Parameters([Object.assign({}, product, {quantity:quantity || 1})], context),
        page: { type: context?.pageType || 'Other', path: context?.path },
        visitor: context?.visitor,
        ecommerce: {
          currencyCode: context?.currencyCode || 'CZK',
          remove: {
            products: [
              Object.assign({}, product, {
                quantity: String(quantity || product.quantity || 1),
              }),
            ],
          },
          basket: {
            products: mapCartToBasketProducts(cart),
          },
        },
      })
    );
  }

  function updateContextProductFromVariant(context, variant) {
    if (!context || !context.product || !variant) {
      return context;
    }

    context.product = {
      id: variant.sku || String(variant.id),
      name: context.productMeta?.title || context.product.name,
      price: formatPrice(variant.price),
      brand: context.productMeta?.vendor || context.product.brand,
      category: context.productMeta?.parentCollectionTitle || context.product.category,
      variant: variant.title || variant.public_title || context.product.variant,
      variantId: variant.id,
    };

    return context;
  }

  function handleCartUpdate(event) {
    const detail = event.detail || {};
    if (detail.data && detail.data.didError) {
      return;
    }

    const cart = detail.resource;
    const source = detail.data && detail.data.source;
    const context = readEnrichedContext();

    if (source === 'product-form-component') {
      const lineItem = findCartLineItem(cart, detail.sourceId);
      if (lineItem) {
        pushAddToCartFromLine(lineItem, detail.data?.itemCount || 1, context, cart);
      }
    } else if (source && ADD_SOURCES.has(source)) {
      const diff = diffCartItems(lastCartSnapshot, cart);
      diff.removed.forEach(function (entry) {
        pushRemoveFromCartFromLine(entry.item, entry.quantity, context, cart);
      });
      diff.added.forEach(function (entry) {
        pushAddToCartFromLine(entry.item, entry.quantity, context, cart);
      });
    } else if (source === CART_CHANGE_SOURCE) {
      const diff = diffCartItems(lastCartSnapshot, cart);
      diff.removed.forEach(function (entry) {
        pushRemoveFromCartFromLine(entry.item, entry.quantity, context, cart);
      });
      diff.added.forEach(function (entry) {
        pushAddToCartFromLine(entry.item, entry.quantity, context, cart);
      });
    }

    lastCartSnapshot = snapshotCart(cart);
  }

  function handleVariantUpdate(event) {
    const context = readEnrichedContext();
    if (!context || context.pageType !== 'Product') {
      return;
    }

    const variant = event.detail && event.detail.resource;
    if (!variant) {
      return;
    }

    updateContextProductFromVariant(context, variant);
    pushPageContext(context);
    pushProductView(context);
  }

  function initializeCartSnapshot(cart) {
    lastCartSnapshot = snapshotCart(cart);
  }

  function bootstrap() {
    const context = readEnrichedContext();
    if (!context) {
      return;
    }

    (context.cartItems || []).forEach(rememberProduct);
    rememberProduct(context.product);
    initializeCartSnapshot({ items: (context.cartItems || []).map(item => Object.assign({}, item, {variant_id:item.variantId,sku:item.id,product_title:item.name,final_price:Math.round(Number(item.price)*100)})) });
    pushPageContext(context);
    pushProductView(context);
    if (context.pageType === 'Cart') pushGa4('view_cart', ga4Parameters(context.cartItems || [], context), context);
    setupProductLists();
    document.addEventListener('visitorConsentCollected', function () { setTimeout(persistCatalog, 0); });

    fetch('/cart.js')
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        initializeCartSnapshot(cart);
      })
      .catch(function () {
        // Keep Liquid-provided snapshot when cart.js is unavailable.
      });

    document.addEventListener('cart:update', handleCartUpdate);
    document.addEventListener('variant:update', handleVariantUpdate);
  }

  bootstrap();

  window.VecickyGtmDataLayer = {
    flushGa4Events: flushGa4Events,
    readContext: readContext,
    mapCartLineItem: mapCartLineItem,
    mapCartToBasketProducts: mapCartToBasketProducts,
    buildPagePayload: buildPagePayload,
    diffCartItems: diffCartItems,
    formatPrice: formatPrice,
    initializeCartSnapshot: initializeCartSnapshot,
    snapshotCart: snapshotCart,
    lastCartLineItems: function () {
      return lastCartLineItems;
    },
  };
})();
